import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Card, EmptyState, LoadingState, SectionHeader, StatusChip, useToast } from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createApprovalsApi } from "../api";
import { approvalStatusTone, formatLabel, subjectHref } from "../labels";
import type { ApprovalDecisionDto, ApprovalDecisionType, ApprovalDto, ApprovalViewer } from "../types";
import { AUTHORITY_COPY, RecordDecisionDialog, WithdrawDialog } from "../components/ApprovalDialogs";
import { expiryLabel } from "./ApprovalsQueuePages";

const DECISION_TONE: Record<ApprovalDecisionType, "success" | "danger" | "warning" | "neutral"> = {
  approved: "success",
  rejected: "danger",
  returned_for_changes: "warning",
  abstained: "neutral",
};

export function ApprovalDetailPage() {
  const { approvalId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const client = useApiClient();
  const api = useMemo(() => createApprovalsApi(client), [client]);

  const [approval, setApproval] = useState<ApprovalDto | null>(null);
  const [decisions, setDecisions] = useState<ApprovalDecisionDto[]>([]);
  const [viewer, setViewer] = useState<ApprovalViewer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; status?: number } | null>(null);
  const [decision, setDecision] = useState<ApprovalDecisionType | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Approvals", to: "/app/approvals" },
    { label: approval?.summary ?? "Approval" },
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.getApproval(approvalId);
      setApproval(r.approval);
      setDecisions(r.decisions);
      setViewer(r.viewer);
    } catch (err) {
      setError({ message: apiErrorMessage(err), ...(err instanceof ApiError ? { status: err.status } : {}) });
    } finally {
      setLoading(false);
    }
  }, [api, approvalId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !approval) return <LoadingState message="Loading approval…" />;
  if (error && !approval) {
    return (
      <EmptyState
        title={error.status === 404 ? "Approval not found" : error.status === 403 ? "You don't have access to Approvals" : "Could not load the approval"}
        description={error.message}
        action={<Button variant="secondary" onClick={() => void navigate("/app/approvals")}>Back to Approvals</Button>}
      />
    );
  }
  if (!approval || !viewer) return null;

  const open = approval.status === "requested" || approval.status === "pending";
  const approvedCount = decisions.filter((d) => d.decision === "approved").length;
  const href = subjectHref(approval.subject_type, approval.subject_id);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={approval.summary ?? formatLabel(approval.approval_type)}
        description={`${formatLabel(approval.approval_type)} · requested by ${approval.requester_user_id === viewer.user_id ? "you" : (approval.requester_name ?? "a staff member")} · ${new Date(approval.requested_at).toLocaleString()}`}
        actions={
          <Flex gap={8} wrap>
            {viewer.can_decide ? (
              <>
                <Button variant="accent" onClick={() => setDecision("approved")}>Approve</Button>
                <Button variant="secondary" onClick={() => setDecision("returned_for_changes")}>Return for changes</Button>
                <Button variant="danger" onClick={() => setDecision("rejected")}>Reject</Button>
              </>
            ) : null}
            {viewer.can_withdraw ? (
              <Button variant="ghost" onClick={() => setWithdrawOpen(true)}>Withdraw</Button>
            ) : null}
          </Flex>
        }
      />

      {open && !viewer.can_decide && viewer.cannot_decide_reason ? (
        <Alert tone="info" title="You can't decide this one">
          {viewer.cannot_decide_reason}
        </Alert>
      ) : null}

      <Flex gap={16} wrap align="start">
        <Stack gap={16} style={{ flex: "2 1 480px", minWidth: 0 }}>
          <Card header={<Text role="title" as="h3">Request</Text>}>
            <Stack gap={12}>
              <Flex gap={8} wrap>
                <StatusChip tone={approvalStatusTone(approval.status)}>{formatLabel(approval.status)}</StatusChip>
                <StatusChip tone="info">{formatLabel(approval.authority_level)}</StatusChip>
              </Flex>
              <Text role="label" as="p">Why</Text>
              <Text role="body" as="p" style={{ whiteSpace: "pre-wrap" }}>
                {approval.reason ?? "No reason was recorded (request predates the reason requirement)."}
              </Text>
            </Stack>
          </Card>

          <Card header={<Text role="title" as="h3">Decisions</Text>}>
            <Stack gap={12}>
              {decisions.length === 0 ? (
                <Text role="small" color="muted" as="p">
                  {open ? "No one has decided yet." : "No decisions were recorded."}
                </Text>
              ) : (
                decisions.map((d) => (
                  <Stack key={d.id} gap={4}>
                    <Flex gap={8} align="center" wrap>
                      <StatusChip tone={DECISION_TONE[d.decision]}>{formatLabel(d.decision)}</StatusChip>
                      <Text role="small" as="span">
                        <strong>{d.approver_user_id === viewer.user_id ? "You" : (d.approver_name ?? "Staff member")}</strong>{" "}
                        · {new Date(d.decided_at).toLocaleString()}
                      </Text>
                    </Flex>
                    {d.decision_reason ? (
                      <Text role="small" color="secondary" as="p" style={{ whiteSpace: "pre-wrap", paddingLeft: 12 }}>
                        {d.decision_reason}
                      </Text>
                    ) : null}
                  </Stack>
                ))
              )}
            </Stack>
          </Card>
        </Stack>

        <Stack gap={16} style={{ flex: "1 1 280px", minWidth: 0 }}>
          <Card header={<Text role="title" as="h3">Rules</Text>}>
            <Stack gap={8}>
              <Text role="small" as="p">{AUTHORITY_COPY[approval.authority_level]}</Text>
              <Text role="small" as="p">
                Approvals: {approvedCount} of {approval.required_decision_count} needed
              </Text>
              <Text role="small" as="p">
                Expires: {approval.expires_at ? `${new Date(approval.expires_at).toLocaleString()} (${expiryLabel(approval.expires_at, approval.status)})` : "No deadline"}
              </Text>
              <Text role="caption" color="muted" as="p">
                The requester can never decide their own request, and each person decides once.
              </Text>
            </Stack>
          </Card>
          <Card header={<Text role="title" as="h3">About</Text>}>
            <Stack gap={8}>
              <Text role="small" as="p">{formatLabel(approval.subject_type)}</Text>
              {href ? (
                <Button variant="secondary" onClick={() => void navigate(href)}>Open the record</Button>
              ) : (
                <Text role="caption" color="muted" as="p">ID {approval.subject_id}</Text>
              )}
              <Button variant="ghost" onClick={() => void navigate(`/app/tasks?view=open&source_type=approval&source_id=${approval.id}`)}>
                Related tasks
              </Button>
            </Stack>
          </Card>
        </Stack>
      </Flex>

      <RecordDecisionDialog
        open={decision !== null}
        decision={decision}
        busy={busy}
        error={dialogError}
        onClose={() => {
          setDecision(null);
          setDialogError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setDialogError(null);
          try {
            await api.recordDecision(approvalId, body);
            toast.push({ title: "Decision recorded", tone: "success" });
            setDecision(null);
            await load();
          } catch (err) {
            setDialogError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <WithdrawDialog
        open={withdrawOpen}
        busy={busy}
        error={dialogError}
        onClose={() => {
          setWithdrawOpen(false);
          setDialogError(null);
        }}
        onSubmit={async (reason) => {
          setBusy(true);
          setDialogError(null);
          try {
            await api.applyApprovalAction(approvalId, "withdraw", { reason });
            toast.push({ title: "Request withdrawn", tone: "success" });
            setWithdrawOpen(false);
            await load();
          } catch (err) {
            setDialogError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}
